import React from 'react';
import { getConfig } from '@klpkonsern/appconfig';
import { AppRoutes } from '@klp/core/app/config';
import { Link } from '@klp/design/Link';

import { Crumb } from './Crumb';
import { TranslationOrChildren } from '@klp/design/TranslationOrChildren';
import type { KlpStyled } from '@klp/design/Theme';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import type { Theme } from '@material-ui/core';
import { Block } from '@klp/design/Block';
import { useBreadcrumbContext } from './BreadcrumbsContext';

const breadcrumbsStyles = (theme: Theme) => ({
    breadcrumbs: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        minWidth: 0,
    },
    breadcrumbLine: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
        paddingRight: theme.spacing(2),
        '& > *': {
            display: 'inline',
        },
    },
});

/**
 *  Breadcrumbs component for displaying the navigation path.
 *  Uses the BreadcrumbContext to get the ordered crumbs.
 *  The first crumb is always the app root, which is linked to the main page.
 *
 * Add crumbs inside <Main> component by using:
 * <Main>
 *   <Main.Breadcrumbs>
 *     <Main.Crumb id="some-id" label="Some Label" href="/some-path" />
 *     <Main.Crumb id="another-id" label="Another Label" />
 *   </Main.Breadcrumbs>
 *  ... rest of the view ...
 * </Main>
 */

export const Breadcrumbs = (props: KlpStyled) => {
    const appRoot = getConfig('WEBAPP_APP_NAME', 'forvalter');
    const classes = useKlpStyles(props, breadcrumbsStyles);
    const { orderedCrumbs } = useBreadcrumbContext();

    return (
        <div className={classes.breadcrumbs}>
            <span className={classes.breadcrumbLine}>
                <Crumb>
                    <Link
                        href={AppRoutes.mainPage()}
                        klpTypographyVariant="small"
                    >
                        <TranslationOrChildren translation={`app.${appRoot}`} />
                    </Link>
                </Crumb>
                {orderedCrumbs.map(({ label, href }, idx) => (
                    <Crumb key={idx}>
                        {href ? (
                            <Link
                                href={href}
                                title={label}
                                klpTypographyVariant="small"
                            >
                                <TranslationOrChildren translation={label} />
                            </Link>
                        ) : (
                            <Block klpTypographyVariant="small">{label}</Block>
                        )}
                    </Crumb>
                ))}
            </span>
        </div>
    );
};
